/**
 * DpaAcceptModal — full-text DPA rendering + cryptographic accept-record.
 *
 * SYNTHESIS_2026-05-05 §3.2 — Buyer #1 (Compliance) needs to read the
 * actual contract and click Accept inside the dashboard. The accept
 * action records:
 *   · documentVersion   (server-canonical, e.g. "2026-05-04")
 *   · documentSha256    (hash of the EXACT text shown in this modal)
 *   · IP-anonymised + user-agent
 *   · chained SHA-256 hash → adds an entry to the OnboardingChainEntry
 *
 * Locale resolves via useLocale → falls back to DE (legal source-of-truth).
 *
 * Accessibility:
 *   · role="dialog", aria-modal, aria-labelledby
 *   · ESC closes, Tab cycles within the modal
 *   · body scroll-lock while open
 *   · returns focus to opener on close
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLegalDoc, useAcceptDpa } from '../../../hooks/useAPI';
import { useLocale } from '../../../hooks/useLocale';

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

interface DpaAcceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccepted?: (sha256: string) => void;
}

const FOCUSABLE_SELECTOR =
  'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  onEscape: () => void,
  active: boolean,
) {
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const moveFocusTimer = window.setTimeout(() => {
      const firstFocusable = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? containerRef.current)?.focus();
    }, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscapeRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusable || focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(moveFocusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef]);
}

/**
 * Tiny markdown renderer — paragraphs, headings (#-####), bold (**text**),
 * inline code (`x`), links [text](url), unordered lists (- ).
 *
 * Intentionally minimal: legal docs don't use tables/images/HTML, so a
 * 50-line renderer beats pulling in a 10KB markdown lib for one modal.
 * Cardinal Rule #4 — never thread untrusted HTML; we explicitly do NOT
 * support raw HTML, only the safe subset above.
 */
function renderMarkdown(body: string): React.ReactElement[] {
  const lines = body.split('\n');
  const out: React.ReactElement[] = [];
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} className="list-disc list-inside text-sm text-muted-foreground mb-4 ml-2 space-y-1">
        {listBuffer.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  const renderInline = (text: string): React.ReactNode => {
    // Order matters: links before bold/code so bracket-bold conflicts don't bite.
    const tokens: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
    const boldRe = /\*\*([^*]+)\*\*/;
    const codeRe = /`([^`]+)`/;
    while (remaining.length > 0) {
      const linkMatch = remaining.match(linkRe);
      const boldMatch = remaining.match(boldRe);
      const codeMatch = remaining.match(codeRe);
      const candidates = [linkMatch, boldMatch, codeMatch].filter(Boolean) as RegExpMatchArray[];
      if (candidates.length === 0) {
        tokens.push(remaining);
        break;
      }
      const earliest = candidates.reduce((a, b) => ((a.index ?? 0) <= (b.index ?? 0) ? a : b));
      const idx = earliest.index ?? 0;
      if (idx > 0) tokens.push(remaining.slice(0, idx));
      if (earliest === linkMatch) {
        tokens.push(
          <a key={key++} href={earliest[2]} target="_blank" rel="noopener noreferrer" className="underline text-foreground hover:no-underline">
            {earliest[1]}
          </a>,
        );
      } else if (earliest === boldMatch) {
        tokens.push(<strong key={key++} className="text-foreground font-semibold">{earliest[1]}</strong>);
      } else {
        tokens.push(<code key={key++} className="text-mono text-xs bg-surface-2 px-1">{earliest[1]}</code>);
      }
      remaining = remaining.slice(idx + earliest[0].length);
    }
    return <>{tokens.map((t, i) => <span key={i}>{t}</span>)}</>;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
      continue;
    }
    flushList();
    if (line.startsWith('# ')) {
      out.push(<h2 key={out.length} className="text-xl font-bold text-foreground mt-6 mb-3">{renderInline(line.slice(2))}</h2>);
    } else if (line.startsWith('## ')) {
      out.push(<h3 key={out.length} className="text-base font-semibold text-foreground mt-5 mb-2">{renderInline(line.slice(3))}</h3>);
    } else if (line.startsWith('### ')) {
      out.push(<h4 key={out.length} className="text-sm font-semibold text-foreground mt-4 mb-2">{renderInline(line.slice(4))}</h4>);
    } else if (line.length === 0) {
      // blank line → paragraph break (already separated by mb-4 on previous block)
    } else {
      out.push(<p key={out.length} className="text-sm text-muted-foreground mb-3">{renderInline(line)}</p>);
    }
  }
  flushList();
  return out;
}

export function DpaAcceptModal({ isOpen, onClose, onAccepted }: DpaAcceptModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { lang } = useLocale();
  const { data: doc, isLoading, error } = useLegalDoc('dpa', lang, { enabled: isOpen });
  const accept = useAcceptDpa();
  const [confirmed, setConfirmed] = useState(false);

  // Block ESC while accept is in flight — same reason as backdrop guard.
  useFocusTrap(dialogRef, () => { if (!accept.isPending) onClose(); }, isOpen);

  // Reset confirmation state every time the modal closes/re-opens.
  useEffect(() => {
    if (!isOpen) setConfirmed(false);
  }, [isOpen]);

  const rendered = useMemo(() => (doc?.body ? renderMarkdown(doc.body) : []), [doc?.body]);

  const handleAccept = async () => {
    try {
      const result = await accept.mutateAsync({
        locale: (lang === 'de' || lang === 'en') ? lang : undefined,
      });
      toast.success('DPA accepted', {
        description: `Version ${result.documentVersion} bound to your audit chain.`,
      });
      onAccepted?.(result.documentSha256 ?? '');
      onClose();
    } catch (err) {
      // Cardinal Rule #4 — no err.message echo. DEV log preserves the
      // redacted diagnostic for engineers.
      if (import.meta.env.DEV) {
        console.warn('[DpaAcceptModal] accept failed:', redactErrorForDev(err));
      }
      toast.error('Could not record DPA acceptance. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={(e) => {
        // Don't close mid-mutation — accept request is in flight, the
        // user-facing toast may still arrive and reopen-then-close-flicker
        // would be confusing.
        if (e.target === e.currentTarget && !accept.isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dpa-modal-title"
        tabIndex={-1}
        className="adp-card border border-border bg-background w-full max-w-3xl max-h-[90vh] flex flex-col focus:outline-none"
      >
        <header className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex flex-col min-w-0">
            <p className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
              GDPR Art. 28 — {lang.toUpperCase()}
            </p>
            <h2 id="dpa-modal-title" className="text-lg font-semibold text-foreground">
              {doc?.title ?? 'Data Processing Agreement'}
            </h2>
            {doc?.version && (
              <p className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mt-0.5">
                Version {doc.version}
                {doc.canonicalUrl && (
                  <>
                    {' · '}
                    <a
                      href={doc.canonicalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline inline-flex items-center gap-1"
                    >
                      Authoritative version <ExternalLink className="size-3" aria-hidden="true" />
                    </a>
                  </>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={accept.isPending}
            className="p-2 min-h-[32px] min-w-[32px] flex items-center justify-center hover:bg-surface-2 disabled:opacity-50 flex-shrink-0"
            aria-label="Close DPA modal"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading agreement…</p>
          )}
          {error && (
            <p className="text-sm text-enforcement">
              Could not load the agreement. Please retry or open the
              authoritative version on the landing page.
            </p>
          )}
          {!isLoading && !error && rendered}
        </div>

        <footer className="flex flex-col gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 rounded-none"
              data-testid="dpa-confirm-checkbox"
            />
            <span className="text-xs text-muted-foreground">
              I have read the agreement above and accept it on behalf of
              my organisation. ADJUDON will record this acceptance with a
              cryptographic hash of this exact text and an IP-anonymised
              timestamp.
            </span>
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="adp-btn px-4 py-2 text-sm"
              disabled={accept.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!confirmed || accept.isPending || isLoading || !doc}
              data-testid="dpa-accept-confirm"
              className="adp-btn adp-btn-primary px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accept.isPending ? (
                'Recording…'
              ) : (
                <>
                  <Check className="size-4" aria-hidden="true" />
                  Accept agreement
                </>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
